import { DragEvent, Suspense, lazy, memo, startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { exportFramesCsv } from '../lib/canlog/exportCsv';
import { exportFramesJson } from '../lib/canlog/exportJson';
import { parseCanLogFileInWorker } from '../lib/canlog/parseInWorker';
import { buildAnalyzerSummary, summarizeCanIds } from '../lib/canlog/summarize';
import type { CanFrame, ParseResult } from '../lib/canlog/types';
import { DecodeContributionModal, type ContributionIntent } from '../components/DecodeContributionModal';
import { EventMarkerPanel } from '../components/decode/EventMarkerPanel';
import { ScenarioSelector } from '../components/decode/ScenarioSelector';
import type { ScenarioPreset } from '../components/decode/ScenarioSelector';
import { SignalGraphPanel } from '../components/decode/SignalGraphPanel';
import { SimplifiedCandidatesPanel } from '../components/decode/SimplifiedCandidatesPanel';
import { VinDetectionCard } from '../components/decode/VinDetectionCard';
import { FooterCTA } from '../components/decode/FooterCTA';
import { WorkspaceStepper } from '../components/decode/WorkspaceStepper';
import { runEventCorrelation, type CorrelationByteResult, type CorrelationRun } from '../lib/decode/correlationEngine';
import { makeEventMarker, type ByteFocusRequest, type EventMarker, type EventType, type ScenarioTag } from '../lib/decode/eventMarkers';
import {
  buildCandidateDbc,
  buildCandidateJson,
  buildDecodedPreview,
  suggestCandidateSignalName
} from '../lib/decode/candidateSignals';
import type { SignalExtractionConfig } from '../lib/decode/bitExtraction';
import { track } from '../lib/analytics';
import {
  fetchSignalHintsForCanIds,
  groupSignalHintsByCanId,
  signalHintBadgeLabel,
  signalHintSourceLabel,
  vehicleContextLabel,
  type SignalHint
} from '../lib/decode/signalHints';
import { buildGraphSeries } from '../lib/decode/graphSeries';
import {
  buildDemoPidSeries,
  parsePidOverlayText,
  runPidCorrelation,
  type PidCorrelationCandidate,
  type PidSeries
} from '../lib/decode/pidOverlay';
import type { ChartOverlaySeries } from '../components/decode/ByteSeriesChart';
import { detectVinBroadcasts } from '../lib/decode/vinDetection';

const ByteBitDiscoveryPanel = lazy(() => import('../components/decode/ByteBitDiscoveryPanel').then((module) => ({ default: module.ByteBitDiscoveryPanel })));
const CorrelationResults = lazy(() => import('../components/decode/CorrelationResults').then((module) => ({ default: module.CorrelationResults })));
const PidOverlayPanel = lazy(() => import('../components/decode/PidOverlayPanel').then((module) => ({ default: module.PidOverlayPanel })));

const FRAME_RENDER_LIMIT = 5000;
const RAW_FRAME_ROW_HEIGHT = 43;
const BUILD_SHA = import.meta.env.VITE_SMARTCAN_BUILD_SHA ?? 'local';
const BUILD_DATE = import.meta.env.VITE_SMARTCAN_BUILD_DATE ?? 'local build';
const REAL_SIENNA_DEMO = {
  url: '/demo/sienna_2011_brake_demo.bin',
  filename: 'sienna_2011_brake_demo.bin',
  demo: 'sienna_2011',
  source: 'public_demo',
  fileSizeBytes: 307664,
  frameCount: 17092,
  durationSeconds: 38.954
};
const MERCEDES_C300_DEMO = {
  url: '/demo/mercedes_c300_2016_demo.bin',
  filename: 'mercedes_c300_2016_demo.bin',
  demo: 'mercedes_c300_2016',
  source: 'public_demo',
  fileSizeBytes: 567962,
  frameCount: 31553,
  durationSeconds: 44.998
};
const GUIDED_DEMO_STORAGE_KEY = 'guided_done';
const GUIDED_DEMO_VERSION = 1;
const TIMESTAMP_WARNING_STORAGE_KEY = 'smartcan_timestamp_warning_dismissed_v1';

type ParseState = 'idle' | 'reading' | 'parsing' | 'success' | 'error';
type DecodeState = 'idle' | 'loading' | 'success' | 'error';
type CorrelationState = 'idle' | 'running' | 'success' | 'error';
type GuidedDemoStep = 'loading' | 'run_correlation' | 'inspect_result';

type DecodeProfile = {
  id: string;
  displayName: string;
  make: string;
  model?: string;
  bus: string[];
  signalCount: number;
  publicSignalCount: number;
  confidenceSummary: {
    value: number;
    label: string;
    evidenceCount: number;
  };
  visibility: string;
};

type DecodedSignal = {
  signalName: string;
  value: number | string | boolean;
  unit?: string;
  source: string;
  visibility: string;
  badge: string;
  confidence: {
    value: number;
    label: string;
    evidenceCount: number;
  };
};

type DecodedFrame = {
  timestampMs?: number;
  canIdHex: string;
  dataHex: string;
  matched: boolean;
  messageName?: string;
  decodedSignals: DecodedSignal[];
};

type DecodeResponse = {
  ok: boolean;
  decodedFrames: DecodedFrame[];
  decodedSignalsCount: number;
  unknownFramesCount: number;
  matchedMessagesCount: number;
  warnings: string[];
  error?: string;
};

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatMs(value: number | null) {
  if (value === null) return 'n/a';
  return `${value.toFixed(3)} ms`;
}

function formatDuration(ms: number) {
  if (ms <= 0) return 'n/a';
  if (ms < 1000) return `${ms.toFixed(1)} ms`;
  return `${(ms / 1000).toFixed(3)} s`;
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function normalizeQuery(value: string) {
  return value.trim().toUpperCase().replace(/\s+/g, ' ');
}

function formatCanIdForAnalytics(canId: number | null | undefined) {
  if (typeof canId !== 'number') return undefined;
  return `0x${canId.toString(16).toUpperCase().padStart(canId > 0x7ff ? 8 : 3, '0')}`;
}

function isRealDemoRoute() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('demo') === 'real';
}

function shouldStartGuidedDemo() {
  if (!isRealDemoRoute()) return false;
  try {
    return window.localStorage.getItem(GUIDED_DEMO_STORAGE_KEY) !== 'true';
  } catch {
    return true;
  }
}

function persistGuidedDemoDone() {
  try {
    window.localStorage.setItem(GUIDED_DEMO_STORAGE_KEY, 'true');
  } catch {
    // localStorage can be unavailable in privacy-restricted contexts.
  }
}

function shouldDismissTimestampWarning() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(TIMESTAMP_WARNING_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistTimestampWarningDismissed() {
  try {
    window.localStorage.setItem(TIMESTAMP_WARNING_STORAGE_KEY, 'true');
  } catch {
    // localStorage can be unavailable in privacy-restricted contexts.
  }
}

function filterFrames(frames: CanFrame[], query: string) {
  const normalized = normalizeQuery(query);
  if (!normalized) return frames;
  const compact = normalized.replace(/[^0-9A-F]/g, '');

  return frames.filter((frame) => {
    const idMatches = frame.canIdHex.toUpperCase().includes(normalized) || frame.canIdHex.replace(/^0X/, '').includes(compact);
    const dataMatches = frame.dataHex.includes(normalized) || frame.dataHex.replace(/\s/g, '').includes(compact);
    return idMatches || dataMatches;
  });
}

const RawFrameRow = memo(function RawFrameRow({
  frame,
  hasSignalHint,
  isSelected,
  onSelectCanId,
  virtualStart
}: {
  frame: CanFrame;
  hasSignalHint: boolean;
  isSelected: boolean;
  onSelectCanId: (canId: number) => void;
  virtualStart: number;
}) {
  const selectFrame = useCallback(() => onSelectCanId(frame.canId), [frame.canId, onSelectCanId]);

  return (
    <tr
      className={`virtual-frame-row ${isSelected ? 'selected-row' : ''}`.trim()}
      onClick={selectFrame}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectFrame();
        }
      }}
      style={{ transform: `translateY(${virtualStart}px)` }}
      tabIndex={0}
    >
      <td>{formatMs(frame.timestampMs)}</td>
      <td>
        <span className="can-id-cell">{frame.canIdHex}</span>
        {hasSignalHint && <span className="known-signal-badge">Signal hint</span>}
      </td>
      <td>{frame.extended ? 'Yes' : 'No'}</td>
      <td>{frame.dlc}</td>
      <td>{frame.dataHex}</td>
      <td>{frame.deltaMs === null ? '' : frame.deltaMs.toFixed(3)}</td>
    </tr>
  );
});

async function readJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error(text.trim().slice(0, 160) || `Request failed with HTTP ${response.status}.`);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(text.trim().slice(0, 160) || 'Server returned an invalid JSON response.');
  }
}

export function AnalyzerPage() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const workspaceRef = useRef<HTMLElement | null>(null);
  const graphRef = useRef<HTMLDivElement | null>(null);
  const byteExplorerRef = useRef<HTMLDivElement | null>(null);
  const rawFrameScrollRef = useRef<HTMLDivElement | null>(null);
  const realDemoAutoLoadRef = useRef(false);
  const realDemoTrackRef = useRef(false);
  const mercedesDemoTrackRef = useRef(false);
  const guidedStartedTrackRef = useRef(false);
  const guidedMarkerAddedRef = useRef(false);
  const guidedAutoCorrelationRef = useRef(false);
  const guidedCompletedTrackRef = useRef(false);
  const [parseState, setParseState] = useState<ParseState>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ParseResult | null>(null);
  const [error, setError] = useState('');
  const [demoError, setDemoError] = useState('');
  const [filter, setFilter] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [decodeProfiles, setDecodeProfiles] = useState<DecodeProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState('generic-obd-ii');
  const [decodeState, setDecodeState] = useState<DecodeState>('idle');
  const [decodeError, setDecodeError] = useState('');
  const [decodeResult, setDecodeResult] = useState<DecodeResponse | null>(null);
  const [contributionIntent, setContributionIntent] = useState<ContributionIntent | null>(null);
  const [signalHints, setSignalHints] = useState<SignalHint[]>([]);
  const [signalHintState, setSignalHintState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [signalHintError, setSignalHintError] = useState('');
  const [selectedGraphCanId, setSelectedGraphCanId] = useState<number | null>(null);
  const [eventMarkers, setEventMarkers] = useState<EventMarker[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [scenarioTag, setScenarioTag] = useState<ScenarioTag>('idle');
  const [selectedScenarioId, setSelectedScenarioId] = useState<ScenarioPreset['id']>('brake');
  const [suggestedEvent, setSuggestedEvent] = useState<{ eventType: EventType; label: string; notes: string; requestId: number } | null>(null);
  const [correlationState, setCorrelationState] = useState<CorrelationState>('idle');
  const [correlationRun, setCorrelationRun] = useState<CorrelationRun | null>(null);
  const [correlationError, setCorrelationError] = useState('');
  const [byteFocus, setByteFocus] = useState<ByteFocusRequest | null>(null);
  const [parseDurationMs, setParseDurationMs] = useState<number | null>(null);
  const [pidSeries, setPidSeries] = useState<PidSeries[]>([]);
  const [selectedPidName, setSelectedPidName] = useState('');
  const [pidResults, setPidResults] = useState<PidCorrelationCandidate[]>([]);
  const [pidCandidate, setPidCandidate] = useState<PidCorrelationCandidate | null>(null);
  const [pidState, setPidState] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const [pidError, setPidError] = useState('');
  const [hideLowActivityIds, setHideLowActivityIds] = useState(false);
  const [guidedMode, setGuidedMode] = useState(() => shouldStartGuidedDemo());
  const [guidedStep, setGuidedStep] = useState<GuidedDemoStep>('loading');
  const [advancedMode, setAdvancedMode] = useState(false);
  const [timestampWarningDismissed, setTimestampWarningDismissed] = useState(() => shouldDismissTimestampWarning());

  const summary = useMemo(() => result && file ? buildAnalyzerSummary(result.frames, file.size) : null, [file, result]);
  const idSummaries = useMemo(() => result ? summarizeCanIds(result.frames) : [], [result]);
  const visibleIdSummaries = useMemo(
    () => hideLowActivityIds ? idSummaries.filter((item) => item.frameCount >= 5) : idSummaries,
    [hideLowActivityIds, idSummaries]
  );
  const deferredFilter = useDeferredValue(filter);
  const filteredFrames = useMemo(() => result ? filterFrames(result.frames, deferredFilter) : [], [deferredFilter, result]);
  const displayedFrames = useMemo(() => filteredFrames.slice(0, FRAME_RENDER_LIMIT), [filteredFrames]);
  const getRawFrameKey = useCallback((index: number) => {
    const frame = displayedFrames[index];
    return frame ? `${frame.index}-${frame.canIdHex}` : index;
  }, [displayedFrames]);
  const rawFrameVirtualizer = useVirtualizer({
    count: displayedFrames.length,
    getScrollElement: () => rawFrameScrollRef.current,
    estimateSize: () => RAW_FRAME_ROW_HEIGHT,
    getItemKey: getRawFrameKey,
    overscan: 24
  });
  const virtualRawFrameRows = rawFrameVirtualizer.getVirtualItems();
  const vinCandidates = useMemo(() => detectVinBroadcasts(result?.frames ?? []), [result]);
  const signalHintsByCanId = useMemo(() => groupSignalHintsByCanId(signalHints), [signalHints]);
  const contributionCanId = displayedFrames[0]?.canIdHex ?? result?.frames[0]?.canIdHex ?? '';
  const selectedEvent = useMemo(() => eventMarkers.find((marker) => marker.id === selectedEventId) ?? null, [eventMarkers, selectedEventId]);
  const selectedCanSummary = useMemo(() => idSummaries.find((item) => item.canId === selectedGraphCanId) ?? null, [idSummaries, selectedGraphCanId]);
  const selectedGraphSeries = useMemo(() => buildGraphSeries(result?.frames ?? [], selectedGraphCanId), [result, selectedGraphCanId]);
  const correlationHasRun = correlationState === 'success' || correlationState === 'error';
  const showTimestampQualityWarning = Boolean(summary?.timestampWarning && correlationHasRun && !timestampWarningDismissed);
  const focusedGraphByte = byteFocus?.canId === selectedGraphCanId ? byteFocus.byteIndex : null;
  const focusedGraphByteLength = pidCandidate?.canId === selectedGraphCanId
    ? Math.max(1, Math.ceil(pidCandidate.length / 8))
    : null;
  const focusedGraphTimestamp = byteFocus?.canId === selectedGraphCanId && byteFocus.evidence
    ? byteFocus.evidence.event_timestamp_ms + (byteFocus.evidence.lag_ms ?? 0)
    : selectedEvent?.timestampMs ?? pidCandidate?.rawPoints[Math.floor(pidCandidate.rawPoints.length / 2)]?.t ?? null;
  const graphOverlays = useMemo<ChartOverlaySeries[]>(() => {
    if (!pidCandidate) return [];
    return [
      {
        id: `pid-${pidCandidate.id}`,
        label: `PID ${pidCandidate.pidName}`,
        color: '#f8fafc',
        points: pidCandidate.pidPoints,
        dashed: true
      },
      {
        id: `can-${pidCandidate.id}`,
        label: `${pidCandidate.canIdHex} ${pidCandidate.byteLabel}`,
        color: '#f7c873',
        points: pidCandidate.rawPoints.map((point) => ({
          t: point.t,
          value: point.value * pidCandidate.slope + pidCandidate.offset
        }))
      }
    ];
  }, [pidCandidate]);
  const decodedRows = useMemo(() => {
    if (!decodeResult) return [];
    return decodeResult.decodedFrames.flatMap((frame) => (
      frame.decodedSignals.map((signal) => ({ frame, signal }))
    ));
  }, [decodeResult]);

  useEffect(() => {
    rawFrameVirtualizer.scrollToOffset(0);
  }, [deferredFilter, rawFrameVirtualizer, result]);

  const workflowSteps: Array<{ text: string; href?: string }> = useMemo(() => {
    if (!result) return [
      { text: 'Start by uploading a capture or trying the demo' }
    ];
    if (eventMarkers.length === 0) return [
      { text: 'Start by ' },
      { text: 'selecting a scenario', href: '#scenario-library' },
      { text: ', then ' },
      { text: 'add an event marker', href: '#event-marker' },
      { text: '.' }
    ];
    if (!correlationRun) return [
      { text: 'Run correlation', href: '#event-correlation' },
      { text: ' to find bytes that changed around the marker.' }
    ];
    if (correlationRun.results.length === 0) return [
      { text: 'No strong result yet. Try ' },
      { text: 'manual inspection', href: '#byte-explorer' },
      { text: ' or ' },
      { text: 'PID overlay', href: '#pid-overlay' },
      { text: '.' }
    ];
    if (!byteFocus) return [
      { text: 'Click a result in ' },
      { text: 'correlation results', href: '#correlation-results' },
      { text: ' to inspect it on the graph and ' },
      { text: 'Byte/Bit Explorer', href: '#byte-explorer' },
      { text: '.' }
    ];
    return [
      { text: 'Validate this candidate using the graph, ' },
      { text: 'Byte/Bit Explorer', href: '#byte-explorer' },
      { text: ', or ' },
      { text: 'PID overlay', href: '#pid-overlay' },
      { text: '.' }
    ];
  }, [byteFocus, correlationRun, eventMarkers.length, result]);

  const currentWorkflowStep: 1 | 2 | 3 | 4 | 5 = useMemo(() => {
    if (eventMarkers.length === 0) return 1;
    if (!correlationRun) return 3;
    if (!byteFocus) return 4;
    return 5;
  }, [byteFocus, correlationRun, eventMarkers.length, result]);

  const unlockedThroughStep: 1 | 2 | 3 | 4 | 5 = useMemo(() => {
    if (byteFocus) return 5;
    if (correlationRun && correlationRun.results.length > 0) return 4;
    if (eventMarkers.length > 0 && selectedEventId) return 3;
    return 2;
  }, [byteFocus, correlationRun, eventMarkers.length, selectedEventId]);

  useEffect(() => {
    let active = true;
    fetch('/api/decode/profiles')
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Decode profiles are unavailable.')))
      .then((payload: { profiles?: DecodeProfile[] }) => {
        if (!active) return;
        const profiles = Array.isArray(payload.profiles) ? payload.profiles : [];
        setDecodeProfiles(profiles);
        if (profiles.length > 0 && !profiles.some((profile) => profile.id === selectedProfileId)) {
          setSelectedProfileId(profiles[0].id);
        }
      })
      .catch(() => {
        if (active) setDecodeProfiles([]);
      });
    return () => {
      active = false;
    };
  }, [selectedProfileId]);

  function prepareRealDemoExplorationScenario() {
    setSelectedScenarioId('brake');
    setScenarioTag('braking');
    setSuggestedEvent({
      eventType: 'brake',
      label: 'Brake press',
      notes: 'Suggested exploration marker for this sanitized capture. Signals are unlabeled; validate candidates with graph and correlation.',
      requestId: Date.now()
    });
  }

  function prepareMercedesDemoExplorationScenario() {
    setSelectedScenarioId('custom');
    setScenarioTag('custom');
    setSuggestedEvent({
      eventType: 'custom',
      label: 'Explore Mercedes capture',
      notes: 'Suggested exploration marker for this sanitized Mercedes C300 capture. Signals are unlabeled; validate candidates with graph and correlation.',
      requestId: Date.now()
    });
  }

  function trackRealDemoLoaded() {
    if (realDemoTrackRef.current) return;
    realDemoTrackRef.current = true;
    track('smartcan_real_demo_loaded', {
      demo: REAL_SIENNA_DEMO.demo,
      source: REAL_SIENNA_DEMO.source,
      file_size_bytes: REAL_SIENNA_DEMO.fileSizeBytes,
      frame_count: REAL_SIENNA_DEMO.frameCount,
      duration_seconds: REAL_SIENNA_DEMO.durationSeconds
    });
  }

  function trackMercedesDemoLoaded() {
    if (mercedesDemoTrackRef.current) return;
    mercedesDemoTrackRef.current = true;
    track('smartcan_mercedes_demo_loaded', {
      demo: MERCEDES_C300_DEMO.demo,
      source: MERCEDES_C300_DEMO.source,
      file_size_bytes: MERCEDES_C300_DEMO.fileSizeBytes,
      frame_count: MERCEDES_C300_DEMO.frameCount,
      duration_seconds: MERCEDES_C300_DEMO.durationSeconds
    });
  }

  function guidedMetadata(extra: Record<string, string | number | boolean> = {}) {
    return {
      demo: 'real',
      guide_version: GUIDED_DEMO_VERSION,
      ...extra
    };
  }

  function completeGuidedMode(reason: string) {
    persistGuidedDemoDone();
    setGuidedMode(false);
    if (guidedCompletedTrackRef.current) return;
    guidedCompletedTrackRef.current = true;
    track('smartcan_guided_completed', guidedMetadata({ reason }));
  }

  async function loadRealSiennaDemo() {
    setDemoError('');
    try {
      const response = await fetch(REAL_SIENNA_DEMO.url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Demo file could not be loaded (${response.status}).`);
      const blob = await response.blob();
      const demoFile = new File([blob], REAL_SIENNA_DEMO.filename, { type: 'application/octet-stream' });
      prepareRealDemoExplorationScenario();
      const parsed = await parseFile(demoFile);
      if (parsed) trackRealDemoLoaded();
    } catch (demoLoadError) {
      setDemoError(demoLoadError instanceof Error ? demoLoadError.message : 'Demo file could not be loaded.');
    }
  }

  async function loadMercedesC300Demo() {
    setDemoError('');
    try {
      const response = await fetch(MERCEDES_C300_DEMO.url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Mercedes demo file could not be loaded (${response.status}).`);
      const blob = await response.blob();
      const demoFile = new File([blob], MERCEDES_C300_DEMO.filename, { type: 'application/octet-stream' });
      prepareMercedesDemoExplorationScenario();
      const parsed = await parseFile(demoFile);
      if (parsed) trackMercedesDemoLoaded();
    } catch (demoLoadError) {
      setDemoError(demoLoadError instanceof Error ? demoLoadError.message : 'Mercedes demo file could not be loaded.');
    }
  }

  async function parseFile(nextFile: File) {
    track('smartcan_upload_started', {
      file_size_bytes: nextFile.size
    });
    setFile(nextFile);
    setResult(null);
    setError('');
    setDemoError('');
    setFilter('');
    setDecodeResult(null);
    setDecodeError('');
    setDecodeState('idle');
    setSignalHints([]);
    setSignalHintError('');
    setSignalHintState('idle');
    setSelectedGraphCanId(null);
    setEventMarkers([]);
    setSelectedEventId(null);
    setCorrelationRun(null);
    setCorrelationError('');
    setCorrelationState('idle');
    setByteFocus(null);
    setPidSeries([]);
    setSelectedPidName('');
    setPidResults([]);
    setPidCandidate(null);
    setPidState('idle');
    setPidError('');
    setHideLowActivityIds(false);
    setParseDurationMs(null);
    setParseState('reading');

    try {
      await new Promise((resolve) => window.setTimeout(resolve, 20));
      setParseState('parsing');
      const startedAt = performance.now();
      const parsed = await parseCanLogFileInWorker(nextFile);
      const nextParseDurationMs = performance.now() - startedAt;
      const parsedSummary = buildAnalyzerSummary(parsed.frames, nextFile.size);
      setParseDurationMs(nextParseDurationMs);
      setResult(parsed);
      setParseState('success');
      track('smartcan_upload_success', {
        frame_count: parsed.frames.length,
        unique_can_ids: new Set(parsed.frames.map((frame) => frame.canId)).size,
        file_size_bytes: nextFile.size,
        parse_time_ms: Math.round(nextParseDurationMs),
        has_timestamp_warning: Boolean(parsedSummary.timestampWarning)
      });
      if (parsed.warnings.length > 0 || parsedSummary.timestampWarning) {
        track('smartcan_parse_warning', {
          frame_count: parsed.frames.length,
          unique_can_ids: parsedSummary.uniqueCanIds,
          warning_count: parsed.warnings.length,
          has_timestamp_warning: Boolean(parsedSummary.timestampWarning)
        });
      }
      return true;
    } catch (parseError) {
      setParseState('error');
      setError(parseError instanceof Error ? parseError.message : 'Unable to parse this capture file.');
      track('smartcan_upload_failed', {
        file_size_bytes: nextFile.size,
        error_type: parseError instanceof Error ? parseError.name : 'unknown'
      });
      return false;
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demo = params.get('demo');
    if (!demo || realDemoAutoLoadRef.current) return;
    realDemoAutoLoadRef.current = true;
    if (demo === 'real' || demo === 'sienna') {
      if (params.get('scenario') === 'brake' || !params.has('scenario')) prepareRealDemoExplorationScenario();
      void loadRealSiennaDemo();
    }
    if (demo === 'mercedes' || demo === 'c300') {
      prepareMercedesDemoExplorationScenario();
      void loadMercedesC300Demo();
    }
  }, []);

  useEffect(() => {
    if (!guidedMode || guidedStartedTrackRef.current) return;
    guidedStartedTrackRef.current = true;
    track('smartcan_guided_started', guidedMetadata());
  }, [guidedMode]);

  useEffect(() => {
    if (!guidedMode || !result || !summary || guidedMarkerAddedRef.current) return;
    guidedMarkerAddedRef.current = true;
    const startMs = summary.firstTimestampMs ?? 0;
    const endMs = summary.lastTimestampMs ?? startMs;
    const timestampMs = Math.max(0, startMs + (endMs - startMs) / 2);
    const marker = makeEventMarker({
      timestampMs,
      eventType: 'brake',
      label: 'Brake press',
      notes: 'Guided demo marker at the midpoint of the sanitized capture. Signals are unlabeled; validate candidates with graph and correlation.'
    });
    setSelectedScenarioId('brake');
    setScenarioTag('braking');
    setSelectedGraphCanId((current) => current ?? idSummaries[0]?.canId ?? null);
    setEventMarkers([marker]);
    setSelectedEventId(marker.id);
    setCorrelationRun(null);
    setCorrelationError('');
    setCorrelationState('idle');
    setGuidedStep('run_correlation');
    track('smartcan_guided_marker_auto_added', guidedMetadata());
  }, [guidedMode, idSummaries, result, summary]);

  useEffect(() => {
    if (!guidedMode || guidedStep !== 'run_correlation' || !result || !selectedEvent || correlationRun || correlationState !== 'idle') return;
    const timer = window.setTimeout(() => {
      if (guidedAutoCorrelationRef.current) return;
      guidedAutoCorrelationRef.current = true;
      track('smartcan_guided_auto_correlation', guidedMetadata());
      runCorrelation({ guidedAuto: true });
    }, 6000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [correlationRun, correlationState, guidedMode, guidedStep, result, selectedEvent]);

  useEffect(() => {
    if (!result) return;
    const uniqueCanIds = Array.from(new Set(result.frames.map((frame) => frame.canId)));
    let active = true;
    setSignalHintState('loading');
    setSignalHintError('');

    fetchSignalHintsForCanIds(uniqueCanIds)
      .then((hints) => {
        if (!active) return;
        setSignalHints(hints);
        setSignalHintState('success');
      })
      .catch(() => {
        if (!active) return;
        setSignalHints([]);
        setSignalHintState('error');
        setSignalHintError('Signal hints unavailable.');
      });

    return () => {
      active = false;
    };
  }, [result]);

  useEffect(() => {
    if (selectedGraphCanId !== null || idSummaries.length === 0) return;
    setSelectedGraphCanId(idSummaries[0].canId);
  }, [idSummaries, selectedGraphCanId]);

  useEffect(() => {
    if (parseState !== 'success' || !result) return;
    window.requestAnimationFrame(() => {
      workspaceRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }, [parseState, result]);

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    const nextFile = event.dataTransfer.files[0];
    if (nextFile) void parseFile(nextFile);
  }

  function exportCsv() {
    if (!result) return;
    track('smartcan_export_csv', {
      frame_count: result.frames.length,
      unique_can_ids: idSummaries.length
    });
    downloadText('smartcan-frames.csv', exportFramesCsv(result.frames), 'text/csv;charset=utf-8');
  }

  function exportJson() {
    if (!result) return;
    track('smartcan_export_json', {
      frame_count: result.frames.length,
      unique_can_ids: idSummaries.length
    });
    downloadText('smartcan-frames.json', exportFramesJson(result.frames), 'application/json;charset=utf-8');
  }

  const toggleAdvancedMode = useCallback(() => {
    startTransition(() => {
      setAdvancedMode((value) => !value);
    });
  }, []);

  const showAdvancedMode = useCallback(() => {
    startTransition(() => {
      setAdvancedMode(true);
    });
  }, []);

  async function decodeFrames() {
    if (!result) return;
    setDecodeState('loading');
    setDecodeError('');
    setDecodeResult(null);

    try {
      const response = await fetch('/api/decode/frames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileId: selectedProfileId,
          frames: result.frames.map((frame) => ({
            timestampMs: frame.timestampMs,
            canId: frame.canId,
            extended: frame.extended,
            dlc: frame.dlc,
            data: frame.data
          }))
        })
      });
      const payload = await readJsonResponse<DecodeResponse>(response);
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || 'Decode failed.');
      }
      setDecodeResult(payload);
      setDecodeState('success');
    } catch (decodeFailure) {
      setDecodeState('error');
      setDecodeError(decodeFailure instanceof Error ? decodeFailure.message : 'Decode failed.');
    }
  }

  function openContribution(intent: ContributionIntent) {
    setContributionIntent(intent);
  }

  const selectCanId = useCallback((canId: number | null) => {
    setSelectedGraphCanId(canId);
    setByteFocus(null);
    setPidCandidate((current) => current?.canId === canId ? current : null);
    if (canId !== null) {
      track('smartcan_graph_can_id_selected', {
        selected_can_id: formatCanIdForAnalytics(canId),
        frame_count: idSummaries.find((item) => item.canId === canId)?.frameCount,
        unique_can_ids: idSummaries.length
      });
    }
  }, [idSummaries]);

  function addEventMarker(marker: EventMarker) {
    setEventMarkers((current) => [...current, marker].sort((a, b) => a.timestampMs - b.timestampMs));
    setSelectedEventId(marker.id);
    setCorrelationRun(null);
    setCorrelationState('idle');
    setCorrelationError('');
    track('smartcan_event_marker_added', {
      scenario_type: scenarioTag,
      event_type: marker.eventType,
      timestamp_ms: Math.round(marker.timestampMs),
      marker_count: eventMarkers.length + 1
    });
  }

  function scenarioTimestamp() {
    const startMs = summary?.firstTimestampMs ?? 0;
    const endMs = summary?.lastTimestampMs ?? startMs;
    return Math.max(0, startMs + (endMs - startMs) / 2);
  }

  function selectScenario(scenario: ScenarioPreset) {
    setSelectedScenarioId(scenario.id);
    setScenarioTag(scenario.scenarioTag);
    setSuggestedEvent({
      eventType: scenario.eventType,
      label: scenario.markerLabel,
      notes: scenario.notes,
      requestId: Date.now()
    });
    track('smartcan_scenario_selected', {
      scenario_type: scenario.scenarioTag,
      event_type: scenario.eventType
    });
  }

  function addSuggestedScenarioMarker(scenario: ScenarioPreset) {
    selectScenario(scenario);
    addEventMarker(makeEventMarker({
      timestampMs: scenarioTimestamp(),
      eventType: scenario.eventType,
      label: scenario.markerLabel,
      notes: scenario.notes
    }));
  }

  function removeEventMarker(eventId: string) {
    setEventMarkers((current) => current.filter((marker) => marker.id !== eventId));
    setSelectedEventId((current) => current === eventId ? null : current);
    setCorrelationRun(null);
  }

  function runCorrelation(options: { guidedAuto?: boolean } = {}) {
    if (!result || !selectedEvent) return;
    track('smartcan_correlation_run', {
      frame_count: result.frames.length,
      unique_can_ids: idSummaries.length,
      scenario_type: scenarioTag,
      event_type: selectedEvent.eventType,
      selected_can_id: formatCanIdForAnalytics(selectedGraphCanId),
      has_timestamp_warning: Boolean(summary?.timestampWarning)
    });
    setCorrelationState('running');
    setCorrelationError('');
    window.setTimeout(() => {
      try {
        const nextRun = runEventCorrelation(result.frames, selectedEvent);
        setCorrelationRun(nextRun);
        setCorrelationState('success');
        if (nextRun.results.length > 0) {
          const topCanId = nextRun.results[0].canId;
          setSelectedGraphCanId(topCanId);
        }
        const eventName = nextRun.results.length > 0 ? 'smartcan_correlation_success' : 'smartcan_correlation_failed';
        track(eventName, {
          frame_count: result.frames.length,
          unique_can_ids: idSummaries.length,
          scenario_type: scenarioTag,
          event_type: selectedEvent.eventType,
          correlation_result_count: nextRun.results.length,
          has_timestamp_warning: Boolean(summary?.timestampWarning)
        });
        if (nextRun.results.length > 0) {
          if (options.guidedAuto && guidedMode) setGuidedStep('inspect_result');
          track('smartcan_evidence_card_viewed', {
            source: 'event_correlation',
            correlation_result_count: nextRun.results.length,
            scenario_type: scenarioTag,
            selected_can_id: nextRun.results[0]?.canIdHex
          });
        }
      } catch (runError) {
        setCorrelationRun(null);
        setCorrelationState('error');
        setCorrelationError(runError instanceof Error ? runError.message : 'Correlation failed.');
        track('smartcan_correlation_failed', {
          frame_count: result.frames.length,
          unique_can_ids: idSummaries.length,
          scenario_type: scenarioTag,
          event_type: selectedEvent.eventType,
          has_timestamp_warning: Boolean(summary?.timestampWarning),
          error_type: runError instanceof Error ? runError.name : 'unknown'
        });
      }
    }, 10);
  }

  function runCorrelationFromUser() {
    if (guidedMode) completeGuidedMode('manual_correlation');
    runCorrelation();
  }

  function correlationFailureReasons() {
    const reasons: string[] = [];
    if (summary && !summary.timestampsMonotonic) reasons.push('timestamps are not monotonic');
    if (summary && !summary.fpsReliable) reasons.push('capture timing span or FPS estimate is unreliable');
    if (selectedEvent && summary && (selectedEvent.timestampMs < (summary.firstTimestampMs ?? 0) || selectedEvent.timestampMs > (summary.lastTimestampMs ?? 0))) {
      reasons.push('event timestamp is outside the capture range');
    }
    if (selectedEvent && result) {
      const nearEvent = result.frames.filter((frame) => Math.abs(frame.timestampMs - selectedEvent.timestampMs) <= 2000).length;
      if (nearEvent < 20) reasons.push('not enough frames near the event marker');
    }
    if (idSummaries.length > 0 && idSummaries.slice(0, 10).every((item) => item.changedCount === 0)) reasons.push('top CAN IDs were mostly static');
    reasons.push('event may not have produced a clear byte change');
    return Array.from(new Set(reasons)).slice(0, 5);
  }

  function tryManualInspection() {
    const nextCanId = idSummaries[0]?.canId ?? null;
    if (nextCanId !== null) setSelectedGraphCanId(nextCanId);
    setByteFocus(null);
    window.requestAnimationFrame(() => {
      graphRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  function draftDefaultsForCorrelation(eventType: EventType | undefined, scenario: ScenarioTag) {
    const scenarioText = scenario === 'custom' ? '' : ` during ${scenario.replace(/_/g, ' ')}`;
    switch (eventType) {
      case 'brake':
        return {
          signalName: 'brake_candidate',
          category: 'brake',
          unit: '',
          notes: `Draft started from a brake correlation result${scenarioText}. Validate with repeated brake events before treating it as a decode.`
        };
      case 'throttle':
        return {
          signalName: 'throttle_candidate',
          category: 'throttle',
          unit: '%',
          notes: `Draft started from a throttle correlation result${scenarioText}. Use known pedal/load values to calculate scale and offset.`
        };
      case 'steering':
        return {
          signalName: 'steering_angle_candidate',
          category: 'steering_angle',
          unit: 'deg',
          notes: `Draft started from a steering correlation result${scenarioText}. Validate left/right direction and center offset.`
        };
      case 'idle':
        return {
          signalName: 'rpm_candidate',
          category: 'rpm',
          unit: 'rpm',
          notes: 'Draft started from an idle event. Compare against a known tachometer or PID RPM value before exporting.'
        };
      case 'cruise':
        return {
          signalName: 'vehicle_speed_candidate',
          category: 'vehicle_speed',
          unit: 'km/h',
          notes: 'Draft started from a cruise event. Compare against known speed or PID speed before exporting.'
        };
      case 'door':
      case 'lights':
        return {
          signalName: `${eventType}_flag_candidate`,
          category: 'flag',
          unit: '',
          notes: `Draft started from a ${eventType} event. Validate this as a binary flag with repeated toggles.`
        };
      default:
        return {
          signalName: 'candidate_signal',
          category: 'other',
          unit: '',
          notes: 'Draft started from a correlation result. Use graph, byte preview, and known values before exporting.'
        };
    }
  }

  function buildByteFocusFromCorrelation(item: CorrelationByteResult, openDraft = false): ByteFocusRequest | null {
    if (!selectedEvent) return null;
    const evidence = {
      event_type: selectedEvent.eventType,
      event_timestamp_ms: selectedEvent.timestampMs,
      scenario_tag: scenarioTag,
      correlation_score: item.score,
      direction: item.direction,
      lag_ms: item.lagMs
    };
    const draftDefaults = draftDefaultsForCorrelation(selectedEvent.eventType, scenarioTag);
    return {
      requestId: Date.now(),
      canId: item.canId,
      byteIndex: item.byteIndex,
      startBit: item.byteIndex * 8,
      length: 8,
      endianness: 'little',
      evidence,
      draft: openDraft ? {
        ...draftDefaults,
        openDraft: true
      } : undefined
    };
  }

  function selectCorrelationResult(item: CorrelationByteResult) {
    const nextFocus = buildByteFocusFromCorrelation(item);
    if (!nextFocus) return;
    setSelectedGraphCanId(item.canId);
    setByteFocus(nextFocus);
    window.requestAnimationFrame(() => {
      graphRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    track('smartcan_correlation_result_clicked', {
      selected_can_id: item.canIdHex,
      scenario_type: scenarioTag,
      event_type: nextFocus.evidence?.event_type,
      byte_index: item.byteIndex,
      correlation_score: Number(item.score.toFixed(3)),
      confidence: item.confidence
    });
    if (guidedMode) completeGuidedMode('result_clicked');
  }

  function createDraftFromCorrelationResult(item: CorrelationByteResult) {
    const nextFocus = buildByteFocusFromCorrelation(item, true);
    if (!nextFocus) return;
    setSelectedGraphCanId(item.canId);
    setByteFocus(nextFocus);
    /* No auto-scroll: the simplified row morphs in place to reveal Export
       buttons right where the user clicked, so jumping the viewport would
       only disorient. Advanced-mode "Inspect graph + bytes" still scrolls
       to the graph via selectCorrelationResult. */
  }

  function buildCorrelationCandidateInput(item: CorrelationByteResult) {
    const config: SignalExtractionConfig = {
      startBit: item.byteIndex * 8,
      length: 8,
      endianness: 'little',
      signed: false,
      factor: 1,
      offset: 0
    };
    const canFrames = (result?.frames ?? []).filter((frame) => frame.canId === item.canId);
    const preview = buildDecodedPreview(canFrames, config, 'other');
    const evidence = selectedEvent ? {
      event_type: selectedEvent.eventType,
      event_timestamp_ms: selectedEvent.timestampMs,
      scenario_tag: scenarioTag,
      correlation_score: item.score,
      direction: item.direction,
      lag_ms: item.lagMs
    } : undefined;
    const signalName = suggestCandidateSignalName({ canIdHex: item.canIdHex, category: 'other', config });
    return {
      canIdHex: item.canIdHex,
      signalName,
      category: 'other' as const,
      selectedByte: item.byteIndex,
      config,
      unit: '',
      notes: 'Generated from correlation result. Validate bit numbering, scale, and offset before treating as a decode.',
      preview,
      scenarioTag,
      evidence
    };
  }

  async function copyToClipboard(text: string) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      textarea.remove();
      return ok;
    } catch {
      return false;
    }
  }

  async function exportCorrelationJson(item: CorrelationByteResult) {
    const text = JSON.stringify(buildCandidateJson(buildCorrelationCandidateInput(item)), null, 2);
    return copyToClipboard(text);
  }

  async function exportCorrelationDbc(item: CorrelationByteResult) {
    const text = buildCandidateDbc(buildCorrelationCandidateInput(item));
    return copyToClipboard(text);
  }

  async function uploadPidFile(nextFile: File) {
    track('smartcan_pid_overlay_opened', {
      source: 'file',
      file_size_bytes: nextFile.size
    });
    setPidError('');
    setPidState('idle');
    setPidResults([]);
    setPidCandidate(null);
    try {
      const text = await nextFile.text();
      const parsed = parsePidOverlayText(text);
      if (parsed.length === 0) throw new Error('No PID samples found. Use CSV/JSON fields: timestamp_ms, pid_name, value.');
      setPidSeries(parsed);
      setSelectedPidName(parsed[0].pidName);
    } catch (failure) {
      setPidError(failure instanceof Error ? failure.message : 'Unable to load PID overlay file.');
    }
  }

  function useDemoPids() {
    track('smartcan_pid_overlay_opened', {
      source: 'demo'
    });
    if (!result) {
      setPidError('Upload a CAN capture before creating demo PID overlays.');
      return;
    }
    const demo = buildDemoPidSeries(result.frames);
    setPidSeries(demo);
    setSelectedPidName(demo[0]?.pidName ?? '');
    setPidResults([]);
    setPidCandidate(null);
    setPidState('idle');
    setPidError('');
  }

  function runPidOverlayCorrelation() {
    if (!result) {
      setPidError('Upload a CAN capture first.');
      return;
    }
    const selectedPid = pidSeries.find((series) => series.pidName === selectedPidName);
    if (!selectedPid) {
      setPidError('Select a PID series first.');
      return;
    }
    setPidState('running');
    setPidError('');
    track('smartcan_pid_correlation_run', {
      frame_count: result.frames.length,
      unique_can_ids: idSummaries.length,
      pid_name: selectedPid.pidName,
      pid_sample_count: selectedPid.points.length,
      selected_can_id: formatCanIdForAnalytics(selectedGraphCanId)
    });
    window.setTimeout(() => {
      try {
        const nextResults = runPidCorrelation(result.frames, selectedPid);
        setPidResults(nextResults);
        setPidCandidate(nextResults[0] ?? null);
        if (nextResults[0]) {
          selectPidCandidate(nextResults[0], false);
        }
        setPidState('success');
        if (nextResults.length > 0) {
          track('smartcan_evidence_card_viewed', {
            source: 'pid_overlay',
            correlation_result_count: nextResults.length,
            selected_can_id: nextResults[0]?.canIdHex,
            pid_name: selectedPid.pidName
          });
        }
      } catch (failure) {
        setPidResults([]);
        setPidCandidate(null);
        setPidState('error');
        setPidError(failure instanceof Error ? failure.message : 'PID correlation failed.');
      }
    }, 10);
  }

  function selectPidCandidate(candidate: PidCorrelationCandidate, trackClick = true) {
    setPidCandidate(candidate);
    setSelectedGraphCanId(candidate.canId);
    setByteFocus({
      requestId: Date.now(),
      canId: candidate.canId,
      byteIndex: Math.floor(candidate.startBit / 8),
      startBit: candidate.startBit,
      length: candidate.length,
      endianness: candidate.endianness
    });
    window.requestAnimationFrame(() => {
      graphRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    if (trackClick) {
      track('smartcan_pid_candidate_clicked', {
        selected_can_id: candidate.canIdHex,
        pid_name: candidate.pidName,
        correlation_score: Number(candidate.correlation.toFixed(3)),
        sample_count: candidate.sampleCount
      });
    }
  }

  function clearPidOverlay() {
    setPidSeries([]);
    setSelectedPidName('');
    setPidResults([]);
    setPidCandidate(null);
    setPidState('idle');
    setPidError('');
  }

  const guidedOverlayCopy = guidedStep === 'inspect_result'
    ? {
        title: 'Correlation complete ✓',
        body: 'Click a result to inspect signal evidence on the graph and byte explorer.'
      }
    : guidedStep === 'run_correlation'
      ? {
          title: 'Marker added ✓',
          body: 'Click Run Correlation to see which CAN bytes reacted.'
        }
      : {
          title: 'Loading guided demo',
          body: 'SmartCAN is preparing a marker and selecting a CAN ID for you.'
        };

  return (
    <main className="analyzer-page">
      {guidedMode && result && (
        <aside className="guided-demo-overlay" role="status" aria-live="polite">
          <div>
            <span className="guided-step-kicker">Guided demo mode</span>
            <strong>{guidedOverlayCopy.title}</strong>
            <p>{guidedOverlayCopy.body}</p>
          </div>
          <button type="button" onClick={() => completeGuidedMode('dismissed')} aria-label="Close guided demo">Close</button>
        </aside>
      )}
      <section className={result ? 'section analyzer-hero parsed' : 'section analyzer-hero'}>
        <div>
          <p className="eyebrow">Client-side capture inspection</p>
          <h1>SmartCAN Web Analyzer</h1>
          <p className="lead">Upload CANLOG01 captures, inspect CAN frames, filter IDs, and export data directly in your browser.</p>
          <p>No login required. Files stay in your browser and are not uploaded to SmartCAN servers.</p>
        </div>
        <div
          className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".bin,.canlog,.log,application/octet-stream,text/plain"
            onChange={(event) => {
              const nextFile = event.currentTarget.files?.[0];
              if (nextFile) void parseFile(nextFile);
            }}
          />
          <p className="eyebrow">Upload capture</p>
          <h2>Drop CANLOG01 / .BIN / .LOG here</h2>
          <p>Drag and drop a capture file, or choose any binary/text CAN log from your device.</p>
          <div className="upload-action-row">
            <button className="button primary" type="button" onClick={() => inputRef.current?.click()}>Choose file</button>
            <button
              className="button secondary real-demo-button"
              type="button"
              disabled={parseState === 'reading' || parseState === 'parsing'}
              onClick={() => void loadRealSiennaDemo()}
            >
              Try Real Sienna Demo
            </button>
            <button
              className="button secondary real-demo-button"
              type="button"
              disabled={parseState === 'reading' || parseState === 'parsing'}
              onClick={() => void loadMercedesC300Demo()}
            >
              Try Mercedes C300 Demo
            </button>
          </div>
          <div className="real-demo-copy">
            <strong>Try Real Sienna Demo</strong>
            <span>Load a sanitized Toyota Sienna 2011 CAN capture to explore graphing and correlation.</span>
            <em>Real sanitized capture. Signals are unlabeled; use correlation and graph tools to explore candidates.</em>
          </div>
          <div className="real-demo-copy">
            <strong>Try Mercedes C300 Demo</strong>
            <span>Load a sanitized Mercedes-Benz C300 2016 CAN capture with VIN-like broadcast data removed.</span>
            <em>Real sanitized capture. Signals are unlabeled; use graph and correlation tools to explore candidates.</em>
          </div>
          <div className={`parse-state ${parseState}`}>Status: {parseState}</div>
          {file && <p className="file-meta">{file.name} · {formatBytes(file.size)}</p>}
          {error && <p className="error-text">{error}</p>}
          {demoError && <p className="error-text">{demoError}</p>}
          {result?.notices?.map((notice) => <p className="info-text" key={notice}>{notice}</p>)}
          {result?.warnings.map((warning) => <p className="warning-text" key={warning}>{warning}</p>)}
        </div>
      </section>

      {result && (
      <>
      {showTimestampQualityWarning && (
        <section className="section analyzer-quality-alert" aria-label="Capture data quality warning">
          <div className="quality-alert-copy">
            <strong>Capture timestamps appear non-monotonic or incomplete.</strong>
            <span>Timestamps in this capture look noisy. Correlation may be less precise.</span>
            <span>You can still inspect CAN IDs manually, use the graph, or validate with PID overlay.</span>
          </div>
          <button
            aria-label="Dismiss timestamp warning"
            className="quality-alert-close"
            type="button"
            onClick={() => {
              setTimestampWarningDismissed(true);
              persistTimestampWarningDismissed();
            }}
          >
            ×
          </button>
        </section>
      )}

      <VinDetectionCard candidates={vinCandidates} />

      <section className="section analyzer-actions">
        <div className="filter-panel">
          <label>Filter CAN ID or data bytes<input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="0x025 or A8 00" /></label>
          <button className="button secondary" type="button" disabled={!filter} onClick={() => setFilter('')}>Clear filter</button>
        </div>
        <div className="export-actions">
          <button className="button secondary" type="button" disabled={!result} onClick={exportCsv}>Export CSV</button>
          <button className="button secondary" type="button" disabled={!result} onClick={exportJson}>Export JSON</button>
        </div>
      </section>

      {advancedMode && (
        <nav className="section analyzer-jump-nav" aria-label="Analyzer workspace shortcuts">
          <a className="button secondary" href="#pid-overlay">PID Overlay</a>
          <a className="button secondary" href="#signal-graph">Graph</a>
          <a className="button secondary" href="#byte-explorer">Byte Explorer</a>
          <a className="button secondary" href="#event-correlation">Correlation</a>
          <a className="button secondary" href="#can-ids">CAN IDs</a>
          <a className="button secondary" href="#raw-frames">Raw Frames</a>
        </nav>
      )}

      <section
        ref={workspaceRef}
        className={`section discovery-workspace-section signal-discovery-workspace${advancedMode ? ' is-advanced' : ' is-default'}`}
        data-bundle-signature="SignalGraph ByteBit EventCorrelation selectedCanId signal-discovery-workspace"
      >
        <div className="workspace-heading">
          <div>
            <p className="eyebrow">Signal Discovery Workspace</p>
            <h2>Signal Discovery Workspace</h2>
            <p className="panel-note">Select a CAN ID to inspect raw bytes, graph changes over time, and correlate events.</p>
          </div>
          <div className="workspace-build-info">
            <span>Build {BUILD_SHA}</span>
            <span>{BUILD_DATE === 'local build' ? BUILD_DATE : new Date(BUILD_DATE).toLocaleString()}</span>
          </div>
        </div>
        <div className="workspace-selected-row">
          <span>Selected CAN ID: <strong>{selectedCanSummary?.canIdHex ?? selectedGraphSeries?.canId ?? 'none'}</strong></span>
          <span>Selected frame count: <strong>{selectedCanSummary?.frameCount.toLocaleString() ?? selectedGraphSeries?.stats.frameCount.toLocaleString() ?? '0'}</strong></span>
          <span>Plotted points: <strong>{selectedGraphSeries?.points.length.toLocaleString() ?? '0'}</strong></span>
        </div>

        <WorkspaceStepper currentStep={currentWorkflowStep} unlockedThrough={unlockedThroughStep} />

        <div className="workspace-mode-toggle-row">
          <button
            className="button ghost workspace-mode-toggle"
            type="button"
            aria-pressed={advancedMode}
            onClick={toggleAdvancedMode}
          >
            {advancedMode ? 'Back to Simple View ▾' : 'Switch to Advanced Mode ▸'}
          </button>
          <small>{advancedMode ? 'Advanced Mode: full engineering tools (graph, byte explorer, raw data).' : 'Simple View: only the top candidates are shown after correlation.'}</small>
        </div>

        <div className="workflow-next-step">
          <strong>Next step</strong>
          <span>
            {workflowSteps.map((part, index) => (
              part.href ? (
                <a className="workflow-anchor" key={`${part.text}-${index}`} href={part.href}>{part.text}</a>
              ) : (
                <span key={`${part.text}-${index}`}>{part.text}</span>
              )
            ))}
          </span>
        </div>

        <div id="scenario-library">
          <ScenarioSelector
            selectedScenarioId={selectedScenarioId}
            onSelectScenario={selectScenario}
            onAddSuggestedMarker={addSuggestedScenarioMarker}
          />
        </div>

        {advancedMode && (
        <div id="pid-overlay" className="workspace-compare-panel">
          <Suspense fallback={null}>
            <PidOverlayPanel
              pidSeries={pidSeries}
              selectedPidName={selectedPidName}
              results={pidResults}
              selectedCandidateId={pidCandidate?.id ?? null}
              isRunning={pidState === 'running'}
              error={pidError}
              onUploadFile={uploadPidFile}
              onUseDemo={useDemoPids}
              onSelectPid={(pidName) => {
                setSelectedPidName(pidName);
                setPidResults([]);
                setPidCandidate(null);
                setPidState('idle');
              }}
              onRunCorrelation={runPidOverlayCorrelation}
              onSelectCandidate={selectPidCandidate}
              onClear={clearPidOverlay}
            />
          </Suspense>
        </div>
        )}

        <div className={`workspace-tool-grid${(advancedMode || correlationRun !== null) ? ' has-tools-column' : ''}`}>
          <section className="section event-correlation-section workspace-tool-card workspace-correlation-card" id="event-correlation">
            <article className="data-panel wide event-correlation-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Signal identification</p>
                  <h2>Event Correlation</h2>
                  <p className="panel-note">Mark what happened, then find which CAN bytes changed around that moment.</p>
                </div>
                <span className={`parse-state ${correlationState}`}>Correlation: {correlationState}</span>
              </div>
              <div id="event-marker">
                <EventMarkerPanel
                  captureStartMs={summary?.firstTimestampMs ?? null}
                  captureEndMs={summary?.lastTimestampMs ?? null}
                  markers={eventMarkers}
                  selectedEventId={selectedEventId}
                  scenarioTag={scenarioTag}
                  correlationBusy={correlationState === 'running'}
                  onAddMarker={addEventMarker}
                  onRemoveMarker={removeEventMarker}
                  onSelectEvent={setSelectedEventId}
                  onScenarioTagChange={setScenarioTag}
                  onRunCorrelation={runCorrelationFromUser}
                  runButtonHighlighted={guidedMode && guidedStep === 'run_correlation'}
                  suggestedEvent={suggestedEvent}
                />
              </div>
              {correlationError && <p className="error-text">{correlationError}</p>}
              {correlationRun ? (
                <div id="correlation-results">
                  {advancedMode ? (
                    <Suspense fallback={null}>
                      <CorrelationResults
                        results={correlationRun.results}
                        warnings={correlationRun.warnings}
                        signalHintsByCanId={signalHintsByCanId}
                        onSelectResult={selectCorrelationResult}
                        onCreateSignalDraft={createDraftFromCorrelationResult}
                        onExportJson={exportCorrelationJson}
                        onExportDbc={exportCorrelationDbc}
                        failureReasons={correlationFailureReasons()}
                        onTryManualInspection={tryManualInspection}
                        highlightTopResult={guidedMode && guidedStep === 'inspect_result'}
                      />
                    </Suspense>
                  ) : (
                    <SimplifiedCandidatesPanel
                      results={correlationRun.results}
                      byteFocusRequestId={byteFocus?.requestId ?? null}
                      onCreateSignalDraft={createDraftFromCorrelationResult}
                      onExportJson={exportCorrelationJson}
                      onExportDbc={exportCorrelationDbc}
                      onShowDetails={showAdvancedMode}
                    />
                  )}
                </div>
              ) : (
                <div className="byte-bit-empty">Choose a scenario above, then add the marker. Correlation results will appear here.</div>
              )}
            </article>
          </section>

          {(advancedMode || correlationRun !== null) && (
          <div className="workspace-tools-column" aria-label="Graph and byte explorer tools">
            <div id="signal-graph" className="workspace-tool-card workspace-graph-card" ref={graphRef}>
              <SignalGraphPanel
                frames={result?.frames ?? []}
                selectedCanId={selectedGraphCanId}
                onSelectCanId={selectCanId}
                signalHintsByCanId={signalHintsByCanId}
                eventMarkers={eventMarkers}
                focusedByteIndex={focusedGraphByte}
                focusedTimestampMs={focusedGraphTimestamp}
                focusedByteLength={focusedGraphByteLength}
                overlays={graphOverlays}
                onZoomUsed={() => track('smartcan_graph_zoom_used', {
                  selected_can_id: formatCanIdForAnalytics(selectedGraphCanId),
                  frame_count: selectedCanSummary?.frameCount,
                  has_timestamp_warning: Boolean(summary?.timestampWarning)
                })}
              />
            </div>

            {advancedMode && (
              <div id="byte-explorer" className="workspace-tool-card workspace-byte-card" ref={byteExplorerRef}>
                <Suspense fallback={null}>
                  <ByteBitDiscoveryPanel
                    frames={result?.frames ?? []}
                    selectedCanId={selectedGraphCanId}
                    signalHintsByCanId={signalHintsByCanId}
                    byteFocus={byteFocus}
                    scenarioTag={scenarioTag}
                    onByteSelected={(byteIndex) => track('smartcan_byte_selected', {
                      selected_can_id: formatCanIdForAnalytics(selectedGraphCanId),
                      byte_index: byteIndex,
                      frame_count: selectedCanSummary?.frameCount
                    })}
                  />
                </Suspense>
              </div>
            )}
          </div>
          )}
        </div>
      </section>

      <section className="section analyzer-summary-section">
        <div className="section-heading compact">
          <p className="eyebrow">Parse summary</p>
          <h2>{result ? 'Capture parsed successfully' : 'Upload a capture to begin'}</h2>
        </div>
        <div className="status-grid">
          <div className="status-card"><small>Total frames</small><strong>{summary?.totalFrames.toLocaleString() ?? '0'}</strong></div>
          <div className="status-card"><small>Unique CAN IDs</small><strong>{summary?.uniqueCanIds.toLocaleString() ?? '0'}</strong></div>
          <div className="status-card"><small>First timestamp</small><strong>{formatMs(summary?.firstTimestampMs ?? null)}</strong></div>
          <div className="status-card"><small>Last timestamp</small><strong>{formatMs(summary?.lastTimestampMs ?? null)}</strong></div>
          <div className="status-card"><small>Capture timestamp span</small><strong>{summary ? formatDuration(summary.durationMs) : 'n/a'}</strong></div>
          <div className={summary && !summary.fpsReliable ? 'status-card fps-warning-card' : 'status-card'}>
            <small>Estimated bus frame rate</small>
            <strong>{summary?.averageFps === null ? 'Unreliable' : summary ? `${summary.averageFps.toFixed(2)} FPS` : 'n/a'}</strong>
            {summary && !summary.fpsReliable && <span className="fps-warning-badge">Estimated frame rate may be unreliable</span>}
          </div>
          <div className="status-card"><small>Browser parse time</small><strong>{parseDurationMs === null ? 'n/a' : formatDuration(parseDurationMs)}</strong></div>
          <div className="status-card"><small>File size</small><strong>{summary ? formatBytes(summary.fileSize) : '0 B'}</strong></div>
        </div>
        {showTimestampQualityWarning && <p className="warning-text">Timestamps in this capture look noisy. Correlation may be less precise.</p>}
      </section>

      <section className="section can-id-summary-section" id="can-ids">
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">CAN ID Summary</p>
              <h2>Frame distribution</h2>
              <p className="panel-note">Click a CAN ID to update the Signal Graph, Byte/Bit Explorer, and Event Correlation workflow.</p>
            </div>
            <span className="table-count">{idSummaries.length.toLocaleString()} IDs</span>
          </div>
          <div className="can-id-tools">
            <label className="toggle-label">
              <input type="checkbox" checked={hideLowActivityIds} onChange={(event) => setHideLowActivityIds(event.target.checked)} />
              Hide low-activity IDs
            </label>
            <span className="panel-note">IDs with fewer than 5 frames are marked as low activity / possible noise.</span>
          </div>
          <div className="table-wrap can-id-summary-wrap">
            <table className="can-table live">
              <thead><tr><th>CAN ID</th><th>Frame count</th><th>Frequency</th><th>DLC</th><th>First seen</th><th>Last seen</th><th>Changed</th></tr></thead>
              <tbody>
                {idSummaries.length === 0 ? (
                  <tr><td colSpan={7}>No CAN ID summaries yet.</td></tr>
                ) : visibleIdSummaries.length === 0 ? (
                  <tr><td colSpan={7}>All visible CAN IDs are hidden by the low-activity filter.</td></tr>
                ) : visibleIdSummaries.map((item) => (
                  <tr
                    className={`${selectedGraphCanId === item.canId ? 'selected-row' : ''} ${item.frameCount < 5 ? 'low-activity-row' : ''}`.trim()}
                    key={item.canIdHex}
                    onClick={() => selectCanId(item.canId)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectCanId(item.canId);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td>
                      <span className="can-id-cell">{item.canIdHex}</span>
                      {item.frameCount < 5 && <span className="low-activity-badge">Low activity / possibly noise</span>}
                    </td>
                    <td>{item.frameCount.toLocaleString()}</td>
                    <td>{item.frequencyHz.toFixed(2)} Hz</td>
                    <td>{item.dlcValues.join(', ')}</td>
                    <td>{formatMs(item.firstSeenMs)}</td>
                    <td>{formatMs(item.lastSeenMs)}</td>
                    <td>{item.changedCount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="section analyzer-table-grid" id="raw-frames">
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Raw frame table</p>
              <h2>Parsed frames</h2>
            </div>
            <span className="table-count">{filteredFrames.length.toLocaleString()} matching frames</span>
          </div>
          <div className="table-wrap raw-frame-wrap virtual-frame-wrap" ref={rawFrameScrollRef}>
            <table className="can-table live virtual-frame-table">
              <thead><tr><th>Timestamp</th><th>CAN ID</th><th>EXT</th><th>DLC</th><th>DATA</th><th>Delta ms</th></tr></thead>
              <tbody
                className="virtual-frame-body"
                style={{ height: `${displayedFrames.length === 0 ? RAW_FRAME_ROW_HEIGHT : rawFrameVirtualizer.getTotalSize()}px` }}
              >
                {displayedFrames.length === 0 ? (
                  <tr className="virtual-frame-empty-row"><td colSpan={6}>{result ? 'No frames match the current filter.' : 'No frames parsed yet.'}</td></tr>
                ) : virtualRawFrameRows.map((virtualRow) => {
                  const frame = displayedFrames[virtualRow.index];
                  return (
                    <RawFrameRow
                      frame={frame}
                      hasSignalHint={signalHintsByCanId[frame.canId]?.length > 0}
                      isSelected={selectedGraphCanId === frame.canId}
                      key={virtualRow.key}
                      onSelectCanId={selectCanId}
                      virtualStart={virtualRow.start}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredFrames.length > FRAME_RENDER_LIMIT && <p className="panel-note">Showing first {FRAME_RENDER_LIMIT.toLocaleString()} frames. Export includes all parsed frames.</p>}
        </article>
      </section>

      <section className="section decode-panel-section">
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Decode Cloud foundation</p>
              <h2>Decode with a safe profile boundary</h2>
              <p className="panel-note">Private Ameriuse definitions run server-side. The browser receives decoded results, confidence, and safe profile metadata only.</p>
            </div>
            <span className={`parse-state ${decodeState}`}>Decode: {decodeState}</span>
          </div>
          <div className="decode-controls">
            <label>
              Decode profile
              <select value={selectedProfileId} onChange={(event) => setSelectedProfileId(event.target.value)}>
                {decodeProfiles.length === 0 ? (
                  <option value="generic-obd-ii">Generic OBD-II Responses</option>
                ) : decodeProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.displayName} - {profile.confidenceSummary.label}
                  </option>
                ))}
              </select>
            </label>
            <button className="button primary" type="button" disabled={!result || decodeState === 'loading'} onClick={decodeFrames}>
              Decode frames
            </button>
            <button className="button secondary" type="button" onClick={() => openContribution('suggest_decode')}>Suggest decode</button>
            <button className="button ghost" type="button" onClick={() => openContribution('upload_evidence')}>Upload evidence</button>
            <button className="button ghost" type="button" onClick={() => openContribution('request_help')}>Request decode help</button>
          </div>
          <p className="panel-note">This is an early contribution system. Signals are reviewed before being added.</p>
          {decodeError && <p className="error-text">{decodeError}</p>}
          {decodeResult?.warnings.map((warning) => <p className="warning-text" key={warning}>{warning}</p>)}
          <div className="status-grid decode-status-grid">
            <div className="status-card"><small>Decoded signals</small><strong>{decodeResult?.decodedSignalsCount.toLocaleString() ?? '0'}</strong></div>
            <div className="status-card"><small>Matched frames</small><strong>{decodeResult?.matchedMessagesCount.toLocaleString() ?? '0'}</strong></div>
            <div className="status-card"><small>Unknown frames</small><strong>{decodeResult?.unknownFramesCount.toLocaleString() ?? '0'}</strong></div>
          </div>
          <div className="table-wrap decode-table-wrap">
            <table className="can-table live">
              <thead><tr><th>Timestamp</th><th>CAN ID</th><th>Message</th><th>Signal</th><th>Value</th><th>Source</th><th>Confidence</th></tr></thead>
              <tbody>
                {decodedRows.length === 0 ? (
                  <tr><td colSpan={7}>{decodeResult ? 'No decoded signals matched this capture yet.' : 'Decode a parsed capture to see signal results.'}</td></tr>
                ) : decodedRows.slice(0, FRAME_RENDER_LIMIT).map(({ frame, signal }, index) => (
                  <tr key={`${frame.canIdHex}-${signal.signalName}-${index}`}>
                    <td>{typeof frame.timestampMs === 'number' ? formatMs(frame.timestampMs) : 'n/a'}</td>
                    <td>{frame.canIdHex}</td>
                    <td>{frame.messageName ?? 'Decoded frame'}</td>
                    <td>{signal.signalName}</td>
                    <td>{String(signal.value)} {signal.unit ?? ''}</td>
                    <td><span className={`source-badge ${signal.badge.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{signal.badge}</span></td>
                    <td>{signal.confidence.label} ({Math.round(signal.confidence.value * 100)}%)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="section signal-hints-section">
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Decode catalog</p>
              <h2>Signal Hints</h2>
              <p className="panel-note">SmartCAN verified records, opendbc seed records, and medium-confidence public dataset records are candidate signal hints for matching CAN IDs. They are not numeric decodes until formula and bit definitions are validated.</p>
            </div>
            <span className={`parse-state ${signalHintState}`}>Hints: {signalHintState}</span>
          </div>
          {signalHintError && <p className="warning-text">{signalHintError}</p>}
          <div className="contribution-list signal-list compact-signal-list signal-hints-scroll">
            {signalHintState === 'loading' ? (
              <article className="decode-contribution-card"><p>Looking up signal hints...</p></article>
            ) : signalHints.length === 0 ? (
              <article className="decode-contribution-card">
                <h3>No signal hints found for this capture yet.</h3>
                <p className="panel-note">You can suggest a candidate signal from this page if you recognize one of the observed CAN IDs.</p>
              </article>
            ) : signalHints.map((hint) => (
              <article className="decode-contribution-card verified-signal-card" key={hint.id}>
                <div className="contribution-card-heading">
                  <div>
                    <p className="eyebrow">{hint.canIdHex}</p>
                    <h3>{hint.signalName}</h3>
                  </div>
                  <span className="signal-hint-badge">{signalHintBadgeLabel(hint)}</span>
                </div>
                <p>{hint.description || 'No description provided yet.'}</p>
                {vehicleContextLabel(hint) && <p className="panel-note">Vehicle/context: {vehicleContextLabel(hint)}</p>}
                {hint.messageName && <p className="panel-note">Message: {hint.messageName}</p>}
                {hint.dataPattern && <p className="panel-note">Pattern: {hint.dataPattern}</p>}
                <div className="signal-meta-row">
                  <span>Source: {signalHintSourceLabel(hint)}</span>
                  <span>Confidence: {hint.confidenceLabel ?? `${Math.round(hint.confidence * 100)}%`}</span>
                </div>
              </article>
            ))}
          </div>
        </article>
      </section>
      </>
      )}
      <DecodeContributionModal
        open={contributionIntent !== null}
        intent={contributionIntent ?? 'suggest_decode'}
        defaultCanId={contributionCanId}
        sourcePage="analyzer"
        onClose={() => setContributionIntent(null)}
      />
      <FooterCTA currentStep={currentWorkflowStep} visible={!advancedMode && Boolean(result)} />
    </main>
  );
}
